import {
	ChangeDetectionStrategy,
	Component,
	inject,
	viewChild,
	computed,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { verifyProperColumns } from '@core/util/csv';
import { parseGhostPracticeCodes } from '@core/util/accounting-codes-csv';
import { ICON } from '@core/util/icon';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessState } from '@state/business';
import { dispatch, select } from '@ngxs/store';
import { DropzoneComponent } from 'src/app/shared/atomic/atoms/dropzone/dropzone.component';
import {
	GhostPracticeCode,
	GhostPracticeCodeTypes,
} from '@models/ghost-practice';
import { DropdownOption } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { SetGhostPracticeCodes } from 'src/app/shared/state/business/business.actions';

@Component({
	selector: 'app-ghost-practice-import-modal',
	templateUrl: './ghost-practice-import-modal.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class GhostPracticeImportModalComponent {
	private readonly setGhostPracticeCodes = dispatch(SetGhostPracticeCodes);
	private readonly matDialogRef = inject(
		MatDialogRef<GhostPracticeImportModalComponent>,
	);
	private readonly snackbarService = inject(SnackbarService);
	private readonly analyticsService = inject(AnalyticsService);

	readonly ICON = ICON;
	storedCodes = select(BusinessState.getGhostPracticeCodes);
	storedFileName = select(BusinessState.getGhostPracticeFileName);

	editableCodes = computed(() =>
		this.storedCodes().map((code) => ({ ...code })),
	);

	modalTitle = computed(() =>
		this.storedFileName() ? 'Review Accounting Codes' : 'Add Accounting Codes',
	);
	readonly codeTypesDropdownOptions: DropdownOption<GhostPracticeCodeTypes>[] =
		[
			{ name: 'Fee', value: GhostPracticeCodeTypes.FEE },
			{ name: 'Disbursement', value: GhostPracticeCodeTypes.DISBURSEMENT },
			{
				name: 'Fee and Disbursement',
				value: GhostPracticeCodeTypes.FEE_AND_DISBURSEMENT,
			},
		];

	readonly codeTypes = GhostPracticeCodeTypes;

	readonly codesFileInput =
		viewChild.required<DropzoneComponent>('codesDropzone');

	async onCodesFileSelected(event: File) {
		try {
			const body = verifyProperColumns(await event.text());

			const { codes } = await parseGhostPracticeCodes({
				csvContent: body,
				existingCodes: this.storedCodes(),
			});

			this.analyticsService.logEvent(
				AnalyticsEvent.GHOSTPRACTICE_BUSINESS_SETTINGS_IMPORT_CODES,
			);

			this.setGhostPracticeCodes(codes, event.name);
		} catch {
			this.snackbarService.openSnackBar({
				text: 'Error: Incompatible CSV file format',
				type: 'error',
			});
		}
	}

	onSubmit(form: NgForm) {
		if (form.invalid) {
			return;
		}

		this.setGhostPracticeCodes(this.editableCodes(), this.storedFileName());
		this.matDialogRef.close();
	}

	trackById(index: number, item: GhostPracticeCode) {
		return item.id;
	}

	openCodesUploadMenu() {
		this.analyticsService.logEvent(
			AnalyticsEvent.GHOST_PRACTICE_BUSINESS_SETTINGS_REIMPORT_CODES,
		);
		this.codesFileInput().openFileUploadMenu();
	}
}
